import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../icons/Icons';
import { computeMachineConfig } from '../../utils/machineConfigLogic';
import { DEFAULT_MACHINE } from '../../constants/machineConstants';
import { useI18n } from '../../context/I18nContext';

/**
 * Modale che mostra la configurazione della macchina spelafili
 * calcolata automaticamente dai dati del cavo multipolare.
 */
const MachineConfigPanel = ({ cable, machineId = DEFAULT_MACHINE, onClose, settings = {} }) => {
    const { t: tGlobal } = useI18n();
    // Stato per i valori inseriti manualmente (override)
    // Chiave: `${sectionId}_${paramKey}` -> Valore
    const [manualValues, setManualValues] = useState({});
    // Slitting: metodo e intervallo segmento per lato L e R
    const [slittingMethod, setSlittingMethod] = useState('multiSegment'); // 'multiSegment' o 'knife'
    const [segmentInterval, setSegmentInterval] = useState(100);

    const config = useMemo(
        () => computeMachineConfig(machineId, cable),
        [machineId, cable]
    );

    // Reset manual values quando cambia il cavo
    useEffect(() => {
        setManualValues({});
    }, [cable]);

    if (!config) return null;

    const t = (key) => tGlobal(`machineConfigPanel.${key}`);

    // Gestione input manuale con calcolo automatico dipendenze
    const handleValueChange = (sectionId, paramKey, value) => {
        const floatVal = parseFloat(value);

        setManualValues(prev => {
            const newValues = { ...prev, [`${sectionId}_${paramKey}`]: value };

            // Controlla se ci sono parametri dipendenti in questa sezione
            const section = config.sections.find(s => s.id === sectionId);
            if (section) {
                section.parameters.forEach(p => {
                    // Se un parametro è linkato a quello modificato (es. Retract -> Blade Value)
                    if (p.linkedTo === paramKey && p.factor !== undefined) {
                        if (!isNaN(floatVal) && value !== '') {
                            // Calcola valore dipendente
                            let calculated = floatVal * p.factor;
                            // Arrotondamento: intero, ceiling al decimale, floor al mezzo, o standard 2 decimali
                            if (p.roundInteger) {
                                calculated = Math.round(calculated);
                            } else if (p.ceilOneDecimal) {
                                calculated = Math.ceil(calculated * 10) / 10;
                            } else if (p.floorHalf) {
                                calculated = Math.floor(calculated * 2) / 2;
                            } else {
                                // Arrotonda a 2 decimali standard
                                calculated = Math.round(calculated * 100) / 100;
                            }
                            newValues[`${sectionId}_${p.key}`] = calculated.toFixed(2);
                        } else {
                            // Se input vuoto, resetta anche il dipendente per coerenza
                            newValues[`${sectionId}_${p.key}`] = '';
                        }
                    }
                });
            }
            return newValues;
        });
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
                    <div>
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <Icons.Settings className="w-5 h-5" />
                            {t('title')} — {config.machineName}
                        </h3>
                        <p className="text-purple-200 text-sm mt-1">{t('subtitle')}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-purple-200 transition-colors p-1"
                    >
                        <Icons.X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4 space-y-4">
                    {config.sections.map((section) => (
                        <div key={section.id} className="rounded-lg border border-gray-200 overflow-hidden">
                            {/* Section Header */}
                            <div className={`px-4 py-2 flex items-center justify-between ${section.id === 'inner'
                                ? section.enabled
                                    ? 'bg-green-50 border-b border-green-200'
                                    : 'bg-gray-50 border-b border-gray-200'
                                : section.type === 'slitting'
                                    ? 'bg-orange-50 border-b border-orange-200'
                                    : section.id === 'general'
                                        ? 'bg-blue-50 border-b border-blue-200'
                                        : 'bg-purple-50 border-b border-purple-200'
                                }`}>
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-sm uppercase tracking-wider text-gray-700">
                                        {section.labelKey ? tGlobal(section.labelKey) : section.label}
                                    </span>
                                    {(section.sublabelKey || section.sublabel) && (
                                        <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full font-medium">
                                            {section.sublabelKey ? tGlobal(section.sublabelKey) : section.sublabel}
                                        </span>
                                    )}
                                </div>
                                {section.id === 'inner' && (
                                    <span className={`text-xs font-bold px-2 py-1 rounded ${section.enabled
                                        ? 'bg-green-200 text-green-800'
                                        : 'bg-gray-200 text-gray-600'
                                        }`}>
                                        {section.enabled ? t('enabled') : t('disabled')}
                                    </span>
                                )}
                            </div>

                            {/* Section Body */}
                            {section.id === 'inner' && !section.enabled ? (
                                <div className="px-4 py-3 text-sm text-gray-500 italic">
                                    {t('innerOff')}
                                </div>
                            ) : section.type === 'slitting' ? (
                                /* === SLITTING SECTION BODY === */
                                <div className="px-4 py-3 space-y-4">
                                    {/* Metodo toggle */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600 font-medium">{tGlobal('machineConfig.EW05.slitting.method')}</span>
                                        <div className="flex rounded-lg overflow-hidden border border-gray-300">
                                            <button
                                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${slittingMethod === 'multiSegment'
                                                        ? 'bg-orange-500 text-white'
                                                        : 'bg-white text-gray-600 hover:bg-gray-100'
                                                    }`}
                                                onClick={() => setSlittingMethod('multiSegment')}
                                            >
                                                {tGlobal('machineConfig.EW05.slitting.multiSegment')}
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 ${slittingMethod === 'knife'
                                                        ? 'bg-orange-500 text-white'
                                                        : 'bg-white text-gray-600 hover:bg-gray-100'
                                                    }`}
                                                onClick={() => setSlittingMethod('knife')}
                                            >
                                                {tGlobal('machineConfig.EW05.slitting.knife')}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Badge Stamp Switch / Stamping Cutting */}
                                    <div className="flex gap-3">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs text-gray-500">Stamp Switch:</span>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${slittingMethod === 'knife'
                                                    ? 'bg-green-200 text-green-800'
                                                    : 'bg-gray-200 text-gray-600'
                                                }`}>
                                                {slittingMethod === 'knife' ? 'ON' : 'OFF'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs text-gray-500">Stamping Cutting:</span>
                                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-green-200 text-green-800">
                                                Stamping Cutting
                                            </span>
                                        </div>
                                    </div>

                                    {/* Intervallo segmenti (solo multi-segment) */}
                                    {slittingMethod === 'multiSegment' && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600 font-medium">
                                                {tGlobal('machineConfig.EW05.slitting.segmentInterval')}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    value={segmentInterval}
                                                    onChange={(e) => setSegmentInterval(Math.max(10, parseInt(e.target.value) || 100))}
                                                    className="w-20 text-right text-sm border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500 py-1"
                                                    min="10"
                                                    step="10"
                                                />
                                                <span className="text-xs text-gray-400">mm</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Tabella unificata slot di incisione (sinistra → destra) */}
                                    {(() => {
                                        const data = section.slittingData;
                                        const SLITTING_THRESHOLD_UI = 300;

                                        // Ricalcola slot con intervallo custom se diverso dal default
                                        const getDisplaySlots = () => {
                                            if (slittingMethod === 'knife') return data.knifeSlots;
                                            if (segmentInterval === data.defaultSegmentInterval) return data.unifiedSlots;

                                            // Ricalcola con intervallo custom usando la stessa logica di machineConstants
                                            const slots = [];

                                            // Left side (A)
                                            if (data.needsSlittingL) {
                                                let lastLeftPos = SLITTING_THRESHOLD_UI;
                                                for (let pos = SLITTING_THRESHOLD_UI + segmentInterval; pos <= data.targetStripL; pos += segmentInterval) {
                                                    slots.push({ startPosition: pos, peelLength: 0.1 });
                                                    lastLeftPos = pos;
                                                }
                                                if (lastLeftPos < data.targetStripL) {
                                                    slots.push({ startPosition: data.targetStripL, peelLength: 0.1 });
                                                }
                                            }

                                            // Right side (B)
                                            if (data.needsSlittingR) {
                                                for (let pos = data.cableLength - data.targetStripR; pos < data.cableLength - SLITTING_THRESHOLD_UI; pos += segmentInterval) {
                                                    slots.push({ startPosition: pos, peelLength: 0.1 });
                                                }
                                            }

                                            slots.sort((a, b) => a.startPosition - b.startPosition);
                                            return slots;
                                        };

                                        const displaySlots = getDisplaySlots();

                                        return (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                {/* Header con info rimanenza per lato */}
                                                <div className="bg-gray-50 px-3 py-2 flex items-center justify-between flex-wrap gap-2">
                                                    <span className="text-xs font-bold text-gray-700 uppercase">Slot incisioni</span>
                                                    <div className="flex gap-3">
                                                        {data.needsSlittingL && (
                                                            <span className="text-xs text-orange-600 font-medium">
                                                                Left +{data.remainingL}mm
                                                            </span>
                                                        )}
                                                        {data.needsSlittingR && (
                                                            <span className="text-xs text-orange-600 font-medium">
                                                                Right +{data.remainingR}mm
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="bg-gray-50 text-gray-500 text-xs">
                                                            <th className="px-3 py-1 text-left w-8">#</th>
                                                            <th className="px-3 py-1 text-right">Start Position (mm)</th>
                                                            <th className="px-3 py-1 text-right">Peel Length (mm)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displaySlots.map((slot, i) => (
                                                            <tr key={i} className="border-t border-gray-100 hover:bg-orange-50 transition-colors">
                                                                <td className="px-3 py-1.5 text-gray-400 text-xs">{String(i + 1).padStart(2, '0')}</td>
                                                                <td className="px-3 py-1.5 text-right font-bold text-orange-700">{slot.startPosition.toFixed(2)}</td>
                                                                <td className="px-3 py-1.5 text-right font-bold text-orange-700">{slot.peelLength.toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    })()}

                                    {/* Avviso metodo knife */}
                                    {slittingMethod === 'knife' && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
                                            ⚠️ {tGlobal('machineConfig.EW05.slitting.knifeWarning')}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {(section.parameters || []).map((param, idx) => {
                                        // Recupera valore: manuale (stato) > calcolato (config)
                                        const manualKey = `${section.id}_${param.key}`;
                                        const hasManualValue = manualValues[manualKey] !== undefined && manualValues[manualKey] !== '';
                                        const displayValue = hasManualValue ? manualValues[manualKey] : (param.value !== null ? param.value : '');

                                        return (
                                            <div key={`${section.id}-${param.key}-${idx}`} className="px-4 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors">
                                                <span className="text-sm text-gray-600 font-medium flex items-center gap-1">
                                                    {param.label}
                                                    {(param.tooltipKey || param.tooltip) && (
                                                        <span
                                                            className="text-blue-400 bg-blue-50 hover:bg-blue-100 hover:text-blue-600 rounded-full w-4 h-4 flex items-center justify-center text-[10px] cursor-help transition-colors border border-blue-200"
                                                            title={
                                                                param.tooltipKey
                                                                    ? tGlobal(param.tooltipKey, param.tooltipParams || {})
                                                                    : param.tooltip
                                                            }
                                                        >
                                                            ?
                                                        </span>
                                                    )}
                                                </span>

                                                {param.manual ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            value={displayValue}
                                                            onChange={(e) => handleValueChange(section.id, param.key, e.target.value)}
                                                            placeholder="—"
                                                            className="w-20 text-right text-sm border-gray-300 rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 py-1"
                                                        />
                                                        <span className="text-xs text-gray-400 w-6">
                                                            {param.unit}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm font-bold text-purple-700 bg-purple-50 px-3 py-1 rounded-md min-w-[3rem] text-right inline-block">
                                                        {typeof displayValue === 'number' ? displayValue.toFixed(2) : displayValue}
                                                        {param.unit && <span className="text-gray-500 font-normal ml-1">{param.unit}</span>}
                                                    </span>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default MachineConfigPanel;
